// Wiki.jsx — top-level Wiki feature shell. Internal view router (browse/doc/ask/admin),
// department tabs, agent dock/full-page, and department context-opening from the
// active app pillar. Mounts inside the real AppLayout (no demo app-rail / role switcher).
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BookOpen, Search, Sparkles, ShieldCheck, Lock, X, FileClock, Archive, RotateCcw, Trash2, Folder, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { usePillar } from '../context/PillarContext';
import { useWikiApi } from '../hooks/useWiki';
import { WIKI_DEPTS, DEPT_BY_ID, PILLAR_TO_WIKI_DEPT, WIKI_LEVEL_RANK } from '../lib/wikiConstants';
import WikiTree from '../components/wiki/WikiTree';
import WikiBrowse from '../components/wiki/WikiBrowse';
import WikiDoc from '../components/wiki/WikiDoc';
import WikiAgent from '../components/wiki/WikiAgent';
import WikiAsk from '../components/wiki/WikiAsk';
import WikiAdmin from '../components/wiki/admin/WikiAdmin';

export default function Wiki() {
    const { user } = useAuth();
    const { activePillarId } = usePillar();
    const api = useWikiApi();
    const [params] = useSearchParams();

    const [boot, setBoot] = useState({ loading: true, locked: false });
    const [accessMap, setAccessMap] = useState({});
    const [isAdmin, setIsAdmin] = useState(false);
    const [activeDept, setActiveDept] = useState('company');
    const [view, setView] = useState('browse');     // browse | doc | ask | admin
    const [docId, setDocId] = useState(null);
    const [tree, setTree] = useState(null);
    const [search, setSearch] = useState('');
    const [agentDept, setAgentDept] = useState('company');
    const [agentOpen, setAgentOpen] = useState(false);
    const [agentFull, setAgentFull] = useState(false);
    const lastPillar = useRef(activePillarId);
    const [createModal, setCreateModal] = useState(null);   // { parentId } when creating a page
    const [archived, setArchived] = useState({ items: [], total: 0 });
    const [archivedOpen, setArchivedOpen] = useState(false);

    // ── Boot: seed (idempotent) → load access → pick department from context ──
    useEffect(() => {
        let m = true;
        (async () => {
            try {
                await api.seed().catch(() => { });
                const acc = await api.access();
                if (!m) return;
                setAccessMap(acc.access || {});
                setIsAdmin(!!acc.is_admin);
                const ctx = PILLAR_TO_WIKI_DEPT[activePillarId];
                const deepLink = params.get('dept');
                const first = WIKI_DEPTS.find(d => acc.access?.[d.id])?.id || 'company';
                const initial = (deepLink && acc.access?.[deepLink] && deepLink)
                    || (ctx && acc.access?.[ctx] && ctx) || first;
                setActiveDept(initial);
                setAgentDept(initial);
                setBoot({ loading: false, locked: false });
            } catch (e) {
                if (!m) return;
                const detail = e?.response?.data?.detail;
                setBoot({ loading: false, locked: detail?.error === 'feature_not_available' });
            }
        })();
        return () => { m = false; };
    }, []); // eslint-disable-line

    const reloadTree = useCallback(() => {
        if (!activeDept) return;
        api.tree(activeDept).then(setTree).catch(() => setTree({ nodes: [], access: null }));
    }, [api, activeDept]);

    useEffect(() => { if (!boot.loading) reloadTree(); }, [activeDept, boot.loading]); // eslint-disable-line

    // Department navigation is driven by the app's green pillar bar: when the
    // active pillar changes while on the Wiki, switch to the matching department.
    useEffect(() => {
        if (boot.loading) return;
        if (activePillarId === lastPillar.current) return;
        lastPillar.current = activePillarId;
        const mapped = PILLAR_TO_WIKI_DEPT[activePillarId];
        if (mapped) { setActiveDept(mapped); setView('browse'); setDocId(null); setSearch(''); }
    }, [activePillarId, boot.loading]);

    // Close the docked agent if its department is no longer accessible.
    useEffect(() => {
        if (agentOpen && !accessMap[agentDept]) setAgentOpen(false);
    }, [accessMap, agentDept, agentOpen]);

    const level = accessMap[activeDept];
    const canEdit = WIKI_LEVEL_RANK[level] >= WIKI_LEVEL_RANK.edit;

    // Archived items for the active dept (editors only — the endpoint requires edit).
    const loadArchived = useCallback(() => {
        if (WIKI_LEVEL_RANK[accessMap[activeDept]] < WIKI_LEVEL_RANK.edit) { setArchived({ items: [], total: 0 }); return; }
        api.archived(activeDept)
            .then(d => setArchived({ items: d.items || [], total: d.total_archived || 0 }))
            .catch(() => setArchived({ items: [], total: 0 }));
    }, [api, activeDept, accessMap]);
    useEffect(() => { if (!boot.loading) loadArchived(); }, [boot.loading, loadArchived]);

    const openDoc = (pageId) => { setDocId(pageId); setView('doc'); };
    const openAgent = (d) => { setAgentDept(d); setAgentFull(false); setAgentOpen(true); if (view === 'ask' || view === 'admin') setView('browse'); };

    const onReorder = async (kind, id, { position, newParentId } = {}) => {
        try {
            const patch = {};
            if (position != null) patch.position = position;
            if (newParentId !== undefined) {
                if (kind === 'page') patch.folder_id = newParentId;   // null = top level
                else patch.parent_id = newParentId;
            }
            await (kind === 'page' ? api.updatePage(id, patch) : api.updateFolder(id, patch));
            reloadTree();
        } catch { toast.error('Could not move'); }
    };
    const onCreate = async (kind, parentId) => {
        if (kind === 'page') { setCreateModal({ parentId }); return; }
        const name = window.prompt('New folder name');
        if (!name) return;
        try { await api.createFolder({ dept: activeDept, name, parent_id: parentId }); reloadTree(); }
        catch { toast.error('Could not create — you may need edit access'); }
    };
    const createPage = async (name, asDraft) => {
        try {
            const p = await api.createPage({ dept: activeDept, name, folder_id: createModal?.parentId ?? null, status: asDraft ? 'draft' : 'published' });
            setCreateModal(null); reloadTree(); openDoc(p.page_id);
        } catch { toast.error('Could not create — you may need edit access'); }
    };
    const importFile = async (file, parentId) => {
        if (!file) return;
        const form = new FormData();
        form.append('file', file);
        form.append('dept', activeDept);
        if (parentId) form.append('folder_id', parentId);
        const t = toast.loading(`Importing “${file.name}”…`);
        try {
            const p = await api.importPage(form);
            toast.success(`Imported “${p.name}”`, { id: t });
            reloadTree(); openDoc(p.page_id);
        } catch (e) {
            const d = e?.response?.data?.detail;
            toast.error(typeof d === 'string' ? d : 'Could not import this file', { id: t });
        }
    };
    const onRename = async (kind, id, currentName) => {
        const name = window.prompt(`Rename ${kind}`, currentName || '');
        if (!name || name.trim() === (currentName || '')) return;
        try { await (kind === 'page' ? api.updatePage(id, { name: name.trim() }) : api.updateFolder(id, { name: name.trim() })); reloadTree(); }
        catch { toast.error('Could not rename'); }
    };
    const onArchive = async (kind, id, name) => {
        const isFolder = kind === 'folder';
        if (isFolder && !window.confirm(`Archive “${name}” and everything inside it?\nYou can restore it any time from Archived.`)) return;
        try {
            await (isFolder ? api.archiveFolder(id) : api.archivePage(id));
            if (!isFolder && id === docId) { setView('browse'); setDocId(null); }
            reloadTree(); loadArchived();
            toast.success(`${isFolder ? 'Folder' : 'Page'} archived`);
        } catch { toast.error('Could not archive — you may need edit access'); }
    };
    const onDelete = async (kind, id, name) => {
        const isFolder = kind === 'folder';
        if (!window.confirm(`Delete “${name}”${isFolder ? ' and everything inside it' : ''} permanently?\nThis cannot be undone.`)) return;
        try {
            await (isFolder ? api.deleteFolder(id) : api.deletePage(id));
            if (!isFolder && id === docId) { setView('browse'); setDocId(null); }
            reloadTree(); loadArchived();
            toast.success(`${isFolder ? 'Folder' : 'Page'} deleted`);
        } catch { toast.error('Could not delete — you may need edit access'); }
    };
    const onRestore = async (kind, id) => {
        try { await (kind === 'folder' ? api.restoreFolder(id) : api.restorePage(id)); reloadTree(); loadArchived(); toast.success('Restored'); }
        catch { toast.error('Could not restore'); }
    };

    const showTree = view === 'browse' || view === 'doc';
    const agentDocked = agentOpen && showTree;
    const mainHidden = agentDocked && agentFull;

    if (boot.loading) {
        return <div className="flex items-center justify-center" style={{ height: 'calc(100vh - 9rem)' }}><div className="text-stone-400 text-[13px]">Loading Wiki…</div></div>;
    }
    if (boot.locked) {
        return (
            <div className="flex items-center justify-center" style={{ height: 'calc(100vh - 9rem)' }}>
                <div className="text-center max-w-sm">
                    <div className="w-12 h-12 rounded-full bg-stone-100 mx-auto flex items-center justify-center text-stone-500"><Lock className="w-5 h-5" /></div>
                    <div className="wiki-display font-bold text-stone-900 mt-3">Wiki isn’t on your plan</div>
                    <div className="text-[13px] text-stone-500 mt-1">The knowledge base is available on Starter and above. Ask an admin to enable it.</div>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col rounded-xl border border-stone-200 overflow-hidden bg-stone-50" style={{ height: 'calc(100vh - 9rem)', minHeight: 560 }}>
            {/* Header */}
            <div className="h-12 px-4 flex items-center gap-2.5 border-b border-stone-200 bg-white/80 backdrop-blur-md shrink-0">
                <button onClick={() => { setActiveDept('company'); setView('browse'); setDocId(null); setSearch(''); }} className="flex items-center gap-2" title="Company home">
                    <div className="w-6 h-6 rounded-md bg-stone-900 text-white flex items-center justify-center"><BookOpen className="w-3.5 h-3.5" /></div>
                    <span className="wiki-display font-bold text-stone-900 text-[14px] tracking-tight">Wiki</span>
                </button>
                <div className="relative ml-2 hidden lg:block">
                    <input value={search} onChange={(e) => { setSearch(e.target.value); if (view !== 'browse') setView('browse'); }}
                        className="w-56 h-8 pl-8 pr-3 rounded-lg border border-stone-200 bg-white text-[12px] placeholder:text-stone-400 focus:outline-none focus:border-stone-600" placeholder="Search this department…" />
                    <Search className="w-3.5 h-3.5 text-stone-500 absolute left-2.5 top-1/2 -transtone-y-1/2" />
                </div>
                <div className="flex-1" />
                <button onClick={() => setView('ask')} className={`h-8 px-3 rounded-lg text-[12px] font-semibold inline-flex items-center gap-1.5 ${view === 'ask' ? 'wiki-grad text-white wiki-shadow-ai' : 'border border-stone-300 text-stone-800 hover:bg-stone-100/50'}`}>
                    <Sparkles className="w-3.5 h-3.5" /> Ask the Wiki
                </button>
                {isAdmin && (
                    <button onClick={() => setView('admin')} className={`h-8 px-3 rounded-lg text-[12px] font-semibold inline-flex items-center gap-1.5 ${view === 'admin' ? 'bg-stone-900 text-white' : 'border border-stone-200 text-stone-600 hover:bg-stone-50'}`}>
                        <ShieldCheck className="w-3.5 h-3.5" /> Admin
                    </button>
                )}
            </div>

            <div className="flex-1 flex min-h-0">
                {showTree && (
                    <div className="w-[256px] border-r border-stone-200 bg-white flex flex-col shrink-0">
                        {tree === null ? (
                            <div className="p-4 text-[12px] text-stone-400">Loading…</div>
                        ) : tree.access ? (
                            <WikiTree deptId={activeDept} access={tree.access} nodes={tree.nodes}
                                selectedId={view === 'doc' ? docId : null} onSelect={openDoc} onOpenAgent={openAgent}
                                canEdit={canEdit} onReorder={onReorder} onCreate={onCreate}
                                onRename={onRename} onArchive={onArchive} onDelete={onDelete} onImport={importFile}
                                onOpenArchived={() => setArchivedOpen(true)} archivedCount={archived.total} />
                        ) : (
                            <div className="p-4 text-[12px] text-stone-400 flex items-center gap-1.5"><Lock className="w-3 h-3" /> No access to this space.</div>
                        )}
                    </div>
                )}
                <div className="flex-1 min-w-0 flex">
                    {!mainHidden && (
                        <div className="flex-1 min-w-0 overflow-hidden">
                            {view === 'browse' && (tree && tree.access === null ? (
                                <NoDeptAccess deptId={activeDept} />
                            ) : <div className="h-full overflow-auto">
                                <WikiBrowse deptId={activeDept} access={tree?.access} nodes={tree?.nodes || []}
                                    filter={search} onSelectDoc={openDoc} onOpenAgent={openAgent} />
                            </div>)}
                            {view === 'doc' && <WikiDoc pageId={docId} onBack={() => { setView('browse'); setDocId(null); }}
                                onOpenAgent={openAgent} onChanged={reloadTree}
                                onArchive={onArchive} onDelete={onDelete} onRestore={onRestore}
                                onLoaded={(dept) => { if (dept && dept !== activeDept) setActiveDept(dept); }} />}
                            {view === 'ask' && <WikiAsk accessMap={accessMap} userName={user?.name} onOpenDoc={openDoc} onBack={() => setView('browse')} />}
                            {view === 'admin' && <WikiAdmin onOpenDoc={openDoc} />}
                        </div>
                    )}
                    {agentDocked && (
                        <div className={agentFull ? 'flex-1 min-w-0' : 'w-[380px] border-l border-stone-200 shrink-0'}>
                            <WikiAgent deptId={agentDept} userName={user?.name} docked={!agentFull} fullPage={agentFull}
                                onToggleFull={() => setAgentFull(f => !f)} onClose={() => setAgentOpen(false)} onOpenDoc={openDoc} />
                        </div>
                    )}
                </div>
            </div>
            {createModal && <CreatePageModal onClose={() => setCreateModal(null)} onCreate={createPage} />}
            {archivedOpen && (
                <ArchivedModal deptName={DEPT_BY_ID[activeDept]?.name} items={archived.items}
                    onClose={() => setArchivedOpen(false)}
                    onOpen={(pageId) => { setArchivedOpen(false); openDoc(pageId); }}
                    onRestore={onRestore} onDelete={onDelete} />
            )}
        </div>
    );
}

function ArchivedModal({ deptName, items, onClose, onOpen, onRestore, onDelete }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/30" onMouseDown={onClose}>
            <div className="w-[520px] max-h-[80vh] flex flex-col bg-white rounded-xl border border-stone-200 wiki-shadow-float" onMouseDown={(e) => e.stopPropagation()}>
                <div className="flex items-center gap-2 px-4 py-3 border-b border-stone-200">
                    <Archive className="w-4 h-4 text-stone-500" />
                    <span className="text-[14px] wiki-display font-bold text-stone-900 flex-1">Archived · {deptName}</span>
                    <button onClick={onClose} className="w-7 h-7 rounded hover:bg-stone-100 text-stone-500 flex items-center justify-center"><X className="w-4 h-4" /></button>
                </div>
                <div className="text-[11px] text-stone-500 px-4 pt-2.5">Restore brings an item (and a folder’s contents) back to the tree. Delete removes it permanently.</div>
                <div className="flex-1 overflow-auto p-3 space-y-1">
                    {(items || []).length === 0 && <div className="text-[12.5px] text-stone-400 px-2 py-8 text-center">Nothing archived in this department.</div>}
                    {(items || []).map(it => {
                        const isFolder = it.kind === 'folder';
                        return (
                            <div key={it.id} className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-stone-50 group">
                                {isFolder ? <Folder className="w-4 h-4 text-amber-500 shrink-0" /> : <FileText className="w-4 h-4 text-stone-500 shrink-0" />}
                                <button disabled={isFolder} onClick={() => !isFolder && onOpen(it.page_id)}
                                    className={`flex-1 min-w-0 text-left text-[13px] truncate ${isFolder ? 'text-stone-700 cursor-default' : 'text-stone-800 hover:underline'}`}>
                                    {it.name}{isFolder && <span className="text-[10px] text-stone-400 ml-1.5">folder</span>}
                                </button>
                                <button onClick={() => onRestore(it.kind, it.id)}
                                    className="opacity-0 group-hover:opacity-100 h-7 px-2 rounded-md border border-stone-200 text-[11px] font-semibold text-stone-700 hover:bg-stone-100 inline-flex items-center gap-1"><RotateCcw className="w-3 h-3" /> Restore</button>
                                <button onClick={() => onDelete(it.kind, it.id, it.name)}
                                    className="opacity-0 group-hover:opacity-100 h-7 px-2 rounded-md text-[11px] font-semibold text-rose-600 hover:bg-rose-50 inline-flex items-center gap-1"><Trash2 className="w-3 h-3" /> Delete</button>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

function CreatePageModal({ onClose, onCreate }) {
    const [name, setName] = useState('');
    const [draft, setDraft] = useState(false);
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/30" onMouseDown={onClose}>
            <div className="w-[420px] bg-white rounded-xl border border-stone-200 wiki-shadow-float p-4" onMouseDown={(e) => e.stopPropagation()}>
                <div className="flex items-center gap-2 mb-3">
                    <span className="text-[14px] wiki-display font-bold text-stone-900 flex-1">New page</span>
                    <button onClick={onClose} className="w-7 h-7 rounded hover:bg-stone-100 text-stone-500 flex items-center justify-center"><X className="w-4 h-4" /></button>
                </div>
                <input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Page title"
                    onKeyDown={(e) => { if (e.key === 'Enter' && name.trim()) onCreate(name.trim(), draft); }}
                    className="w-full h-9 px-3 rounded-lg border border-stone-200 text-[13px] focus:outline-none focus:border-stone-400" />
                <label className="flex items-start gap-2 mt-3 cursor-pointer">
                    <input type="checkbox" checked={draft} onChange={(e) => setDraft(e.target.checked)} className="mt-0.5 accent-stone-900" />
                    <span className="text-[12px] text-stone-600"><span className="inline-flex items-center gap-1 font-semibold text-stone-800"><FileClock className="w-3 h-3 text-amber-500" /> Save as draft</span><br />Only you and admins can see it until you publish.</span>
                </label>
                <div className="flex justify-end gap-2 mt-4">
                    <button onClick={onClose} className="h-9 px-3 rounded-lg border border-stone-200 text-[12px] font-semibold text-stone-600">Cancel</button>
                    <button onClick={() => name.trim() && onCreate(name.trim(), draft)} disabled={!name.trim()}
                        className="h-9 px-4 rounded-lg bg-stone-900 text-white text-[12px] font-semibold disabled:opacity-50">Create</button>
                </div>
            </div>
        </div>
    );
}

function NoDeptAccess({ deptId }) {
    const dept = DEPT_BY_ID[deptId];
    return (
        <div className="h-full flex items-center justify-center p-10">
            <div className="text-center max-w-sm">
                <div className="w-12 h-12 rounded-full bg-stone-100 mx-auto flex items-center justify-center text-stone-500"><Lock className="w-5 h-5" /></div>
                <div className="wiki-display font-bold text-stone-900 mt-3">{dept?.name || 'This space'} is restricted</div>
                <div className="text-[13px] text-stone-500 mt-1">The {dept?.name || 'selected'} wiki isn’t shared with your role. Ask a workspace admin for access.</div>
            </div>
        </div>
    );
}
